import { useState } from 'react';
import { createClient } from '@supabase/supabase-js';
import { SUPABASE_URL, SUPABASE_KEY } from '@/components/utils/constants';

export interface CreateSharedPaymentResult {
  success: boolean;
  url?: string;
  /**
   * `itinerary_already_paid` e' il guard server sul doppio
   * pagamento: la sessione non viene creata perche' su questo itinerario c'e' gia' un
   * addebito differito in volo, o perche' e' gia' saldato.
   */
  error?:
    | 'link_not_found'
    | 'link_expired'
    | 'invalid_password'
    | 'stripe_not_configured'
    | 'itinerary_already_paid'
    | 'legal_not_accepted'
    | 'network_error'
    | string;
}

export const useSharedPayment = (shareToken: string) => {
  const [isCreatingPayment, setIsCreatingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  /**
   * `installmentId` valorizzato paga UNA rata del piano invece dell'intero itinerario:
   * l'edge function usa l'importo di quella rata e ne mette l'id nei metadata Stripe,
   * cosi' il webhook sa quale rata marcare come incassata.
   *
   * `legalAccepted` e' la spunta dei termini: il server la esige (e ne registra la
   * traccia) quando l'agenzia ha compilato i documenti, quindi va passata sempre — un
   * pulsante abilitato per errore qui si ferma comunque la' con `legal_not_accepted`.
   */
  const createPaymentLink = async (
    password?: string,
    customerEmail?: string,
    installmentId?: string,
    legalAccepted?: boolean,
  ): Promise<CreateSharedPaymentResult> => {
    setIsCreatingPayment(true);
    setPaymentError(null);

    try {
      // Create a fresh Supabase client without auth
      const supabase = createClient(SUPABASE_URL, SUPABASE_KEY);

      const payload: Record<string, string | boolean> = { shareToken };
      if (password) {
        payload.password = password;
      }
      if (customerEmail) {
        payload.customerEmail = customerEmail;
      }
      if (installmentId) {
        payload.installmentId = installmentId;
      }
      if (legalAccepted) {
        payload.legalAccepted = true;
      }

      const { data: result, error: err } = await supabase.functions.invoke(
        'create-shared-payment-link',
        {
          body: JSON.stringify(payload),
        }
      );
      
      if (err) {
        console.error('Edge function error:', err);
        setPaymentError('network_error');
        return { success: false, error: 'network_error' };
      }
      
      if (!result || !result.success) {
        const errorMsg = result?.error || 'unknown_error';
        setPaymentError(errorMsg);
        return { success: false, error: errorMsg };
      }
      
      return { success: true, url: result.url };
    } catch (e) {
      console.error('Network error:', e);
      setPaymentError('network_error');
      return { success: false, error: 'network_error' };
    } finally {
      setIsCreatingPayment(false);
    }
  };

  return { createPaymentLink, isCreatingPayment, paymentError };
};
